import type { CSSProperties } from "react";
import hallImg from "../stepper/steptypes/sm-doors-hall.webp";
import hardwareImg from "../stepper/steptypes/sm-doors-hardware.webp";
import defaultDoor from "../stepper/steptypes/door-default.webp";
export interface DoorOffer {
  day: string;
  doors: {
    family: string;
    label: string;
    image?: string;
    missionsByLevel: Record<string, number>;
  }[];
  completed?: number;
}
const TITLE_Y = "23.3%";
// (the screens there are 9.6% of the image tall — crossbeam to door frame —
// and grow for a title too long to fit)

/**
 * Each door's opening in the art, as fractions of the image (x, y, w, h) — the
 * same boxes scripts/build-doors-art.py fits the doors into. `hinge` is the
 * side away from the handle, which the door swings on when it opens.
 */
const DOOR_OPENING = {
  left: {
    x: 46 / 937,
    y: 512 / 1678,
    w: 344 / 937,
    h: 689 / 1678,
    hinge: "left",
  },
  right: {
    x: 542 / 937,
    y: 512 / 1678,
    w: 347 / 937,
    h: 689 / 1678,
    hinge: "right",
  },
} as const;
const IMG_W = "calc(100dvh * 937 / 1678)";
/** Breathing room between the label and the screen edge. */
const PAD = "10px";
/** The player frame's width (see routes/index.tsx: capped by aspect). */
const FRAME_W = "min(100vw, 100dvh * 0.62)";

/** An image-fraction x as a viewport position (the art is centered). */
const atX = (frac: number) => `calc(50% + ${frac - 0.5} * ${IMG_W})`;

// On a phone the art is wider than the screen, so a label centred on its door
// runs off the edge. Each label spans the part of its door that is actually visible, and is
// centred on the door where that fits, otherwise nudged inward.
/** Each sign's nudge off its door's centre, as a fraction of the image. */
const SIGN_NUDGE = { left: 0, right: 18 / 937 };
const labelBox = (side: "left" | "right") => {
  const d = { ...DOOR_OPENING[side] };
  d.x += SIGN_NUDGE[side];
  // The label lives in the art (so it rides the walk-through zoom), and the
  // art is centred on the screen, so the screen's edges are 50% ± half of it.
  const lo = `max(50% - ${FRAME_W} / 2 + ${PAD}, ${atX(d.x)})`;
  const hi = `min(50% + ${FRAME_W} / 2 - ${PAD}, ${atX(d.x + d.w)})`;
  const width = `min(${IMG_W} * ${d.w}, ${hi} - ${lo})`;
  const center = atX(d.x + d.w / 2);
  return {
    width,
    left: `calc(clamp(${lo} + ${width} / 2, ${center}, ${hi} - ${width} / 2))`,
  };
};

/** A door's opening as a box in the art. */
const doorBox = (d: { x: number; y: number; w: number; h: number }) => ({
  left: `${d.x * 100}%`,
  top: `${d.y * 100}%`,
  width: `${d.w * 100}%`,
  height: `${d.h * 100}%`,
});

/** A mission's door art, trimmed a little each side and stretched into the
 *  opening (the same fit scripts/build-doors-art.py used when it was baked). */
const FACE_TRIM = 0.03;
const FACE_SIZE = `${100 / (1 - 2 * FACE_TRIM)}% 100%`;

const Label = ({
  side,
  text,
  color,
  ready,
}: {
  side: "left" | "right";
  text: string;
  color: string;
  ready: boolean;
}) => {
  if (!text) return null;
  // Each door's label colour is authored, and it drives both the text and its
  // glow, so it rides along as a custom property. It sits in the wall panel
  // above its door (labelBox / TITLE_Y) — the door art is busy enough that
  // text laid over the door itself does not read — on a display screen set
  // into the stone there: part of the picture from the first frame (dark
  // glass), powering on in the door's colour when the doors are ready.
  // Longer titles set smaller (by area: size falls with √length) so they
  // still fit the screen.
  const fontCqw = Math.min(3.8, 3.8 * Math.sqrt(22 / text.length));
  const vars = {
    "--sm-door": color,
    "--sm-title-y": TITLE_Y,
    ...labelBox(side),
    fontSize: `clamp(9.5px, ${fontCqw.toFixed(2)}cqw, 20px)`,
  } as CSSProperties;
  return (
    <div
      style={vars}
      className="pointer-events-none absolute top-[var(--sm-title-y)] z-[2] flex min-h-[9.6cqh] -translate-x-1/2 -translate-y-1/2"
    >
      {/* once lit, its light spills on the stone around it */}
      <div
        className={`absolute -inset-[3cqw] rounded-[50%] bg-[radial-gradient(closest-side,color-mix(in_srgb,var(--sm-door)_22%,transparent),transparent)] blur-[1.2cqw] transition-opacity duration-700 ${
          ready ? "opacity-100" : "opacity-0"
        }`}
      />
      {/* the bars it hangs from, up to the crossbeam */}
      <div className="absolute bottom-full left-[18%] h-[3cqh] w-[1.1cqw] bg-[linear-gradient(90deg,#141312,#4a4642_45%,#141312)] shadow-[0_0_4px_rgba(0,0,0,.8)]" />
      <div className="absolute right-[18%] bottom-full h-[3cqh] w-[1.1cqw] bg-[linear-gradient(90deg,#141312,#4a4642_45%,#141312)] shadow-[0_0_4px_rgba(0,0,0,.8)]" />
      {/* set into the wall: a recess cut in the stone (shadowed at the top,
          catching the light on its lower lip), a dull gunmetal frame, glass */}
      <div className="relative flex flex-1 rounded-[.8cqw] bg-[#0a0a0b] p-[.8cqw] shadow-[inset_0_.8cqw_1.4cqw_rgba(0,0,0,.95),0_1px_0_rgba(255,236,200,.14),0_-1px_0_rgba(0,0,0,.6)]">
        <div className="relative flex flex-1 rounded-[.6cqw] bg-[linear-gradient(180deg,#2c2b2a,#1b1a1a_50%,#262422)] p-[.7cqw] shadow-[inset_0_1px_0_rgba(255,240,220,.12),0_1px_2px_rgba(0,0,0,.8)]">
          <div
            className={`relative flex flex-1 items-center justify-center overflow-hidden rounded-[.4cqw] bg-[#020304] px-[2cqw] py-[1.2cqw] text-center font-sans leading-[1.2] font-extrabold tracking-[.04em] text-balance uppercase shadow-[inset_0_0_1.6cqw_rgba(0,0,0,.9)] ${
              ready ? "" : "text-transparent"
            }`}
          >
            {/* the lit panel: dark glass until the doors are ready, then it
                powers on in the door's colour */}
            <div
              className={`absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,color-mix(in_srgb,var(--sm-door)_18%,#04070a),#020304_85%)] ${
                ready ? "" : "opacity-0"
              }`}
            />
            <span
              className={`relative text-[color-mix(in_srgb,var(--sm-door)_70%,white)] [text-shadow:0_0_.6cqw_var(--sm-door),0_0_2cqw_color-mix(in_srgb,var(--sm-door)_55%,transparent)] ${
                ready ? "" : "opacity-0"
              }`}
            >
              {text}
            </span>
            {/* the panel's pixel grid, curved-glass shading, and a reflection */}
            <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,rgba(0,0,0,.18)_0_1px,transparent_1px_3px),repeating-linear-gradient(0deg,rgba(0,0,0,.12)_0_1px,transparent_1px_3px)]" />
            <div className="absolute inset-0 shadow-[inset_0_0_2.2cqw_rgba(0,0,0,.85)]" />
            <div className="absolute inset-x-0 top-0 h-[40%] bg-[linear-gradient(170deg,rgba(255,255,255,.06),transparent_60%)]" />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Settled artwork and signs from WebSmDoors, with no hooks or player actions. */
export function DoorOfferPreview({
  offer,
  options,
}: {
  offer: DoorOffer;
  options: Record<string, string>;
}) {
  const imageUrl = (url: string | undefined) =>
    url && (/^https:\/\//i.test(url) || url.startsWith("/"))
      ? url
      : defaultDoor;
  const background = (url: string) => ({
    backgroundImage: `url(${JSON.stringify(url)})`,
  });
  return (
    <section
      style={{
        height: "100dvh",
        position: "relative",
        overflow: "hidden",
        background: "#000",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          height: "100%",
          aspectRatio: "937 / 1678",
          transform: "translate(-50%,-50%)",
          containerType: "size",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            ...background(hallImg),
            position: "absolute",
            inset: 0,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {(["left", "right"] as const).map((side, i) => {
          const door = offer.doors.find(
            (d) => d.family === (i === 0 ? "love" : "stranger"),
          );
          return door ? (
            <div
              key={side}
              style={{
                ...doorBox(DOOR_OPENING[side]),
                ...background(imageUrl(door.image)),
                backgroundSize: FACE_SIZE,
                backgroundPosition: "center",
                position: "absolute",
              }}
            />
          ) : null;
        })}
        <div
          style={{
            ...background(hardwareImg),
            position: "absolute",
            inset: 0,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {(["left", "right"] as const).map((side, i) => (
          <Label
            key={side}
            side={side}
            text={
              offer.doors.find(
                (d) => d.family === (i === 0 ? "love" : "stranger"),
              )?.label || ""
            }
            color={i === 0 ? "#8fbfe8" : "#e8b74e"}
            ready={true}
          />
        ))}
      </div>
      {options.Title ? (
        <div
          style={{
            position: "absolute",
            top: 26,
            left: 0,
            right: 0,
            textAlign: "center",
            padding: "0 20px",
            color: "#eadfc6",
          }}
        >
          <div
            className="font-stencil"
            style={{
              fontSize: 10.5,
              letterSpacing: 4,
              color: "#d6a94e",
              marginBottom: 9,
            }}
          >
            ▸ Clearance Required ◂
          </div>
          <div
            className="font-stencil"
            style={{
              fontSize: 18,
              lineHeight: 1.3,
              letterSpacing: 1.2,
              textTransform: "uppercase",
            }}
          >
            {options.Title}
          </div>
        </div>
      ) : null}
    </section>
  );
}
